import type { MantineColor } from '@mantine/core';
import { ChatChartPointDto, ChatChartSpecDto } from 'src/api/FiretrolApiClient';
import { formatCurrency } from 'src/utils';

// Validated for colorblind separation, lightness band, and contrast in light and dark mode; never reorder or cycle.
export const CHAT_CHART_COLORS: readonly MantineColor[] = [
  'blue.7',
  'orange.8',
  'teal.7',
  'grape.7',
  'lime.9',
  'cyan.7',
  'pink.7',
  'indigo.7',
];

export type ChatChartType = 'bar' | 'stacked-bar' | 'line' | 'area' | 'pie';

export type DisplayableChatChartSpec = ChatChartSpecDto & {
  type: ChatChartType;
};

const CHART_TYPES: readonly string[] = [
  'bar',
  'stacked-bar',
  'line',
  'area',
  'pie',
];

export const CHAT_PIE_SIZE = 200;

/** The slices a pie can draw: a zero, negative or missing value has no area. */
export const drawablePieSlices = <T extends { value: number | null }>(
  slices: T[],
): (T & { value: number })[] =>
  slices.filter(
    (slice): slice is T & { value: number } =>
      slice.value !== null && slice.value > 0,
  );

export const CHART_EMPTY_VALUE = '—';

/** True when the spec can be drawn: a known type, at least one series and one point, and no more series (or pie slices) than there are palette colors. */
export const isDisplayableChart = (
  spec: ChatChartSpecDto,
): spec is DisplayableChatChartSpec => {
  const seriesCount = spec.series?.length ?? 0;
  const pointCount = spec.points?.length ?? 0;
  const colorsNeeded = spec.type === 'pie' ? pointCount : seriesCount;
  return (
    CHART_TYPES.includes(spec.type) &&
    seriesCount > 0 &&
    pointCount > 0 &&
    colorsNeeded <= CHAT_CHART_COLORS.length
  );
};

/** The value of series `seriesIndex` at a point, or null when missing or not a finite number. */
export const chartValueAt = (
  point: ChatChartPointDto,
  seriesIndex: number,
): number | null => {
  const value = point.values?.[seriesIndex];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

// Series labels are model-written text, so the recharts data keys are
// synthetic: a label can never collide with the x key or with another label.
export const CHART_X_KEY = 'x';
export const chartSeriesKey = (index: number): string => `s${index}`;

/** One recharts data row per point, keyed by `CHART_X_KEY` and `chartSeriesKey`. */
export const chartDataRows = (
  spec: DisplayableChatChartSpec,
): Record<string, string | number | null>[] =>
  spec.points.map((point) => {
    const row: Record<string, string | number | null> = {
      [CHART_X_KEY]: point.x,
    };
    spec.series.forEach((_, index) => {
      row[chartSeriesKey(index)] = chartValueAt(point, index);
    });
    return row;
  });

const countFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});
const decimalFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const percentFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});
const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Formats a chart value by its series unit: currency as USD with cents,
 * percent as an already-scaled percent number (42.5 → "42.5%"), hours with an
 * "h" suffix, count as a grouped integer, anything else grouped with up to 2
 * decimals.
 * @registry Unit-aware value formatter for AI chat chart specs (tooltip, data table, transcript export).
 */
export const formatChartValue = (
  value: number | null | undefined,
  unit: string,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return CHART_EMPTY_VALUE;
  }
  switch (unit) {
    case 'currency':
      return formatCurrency(value, { precise: true });
    case 'percent':
      return `${percentFormatter.format(value)}%`;
    case 'hours':
      return `${decimalFormatter.format(value)} h`;
    case 'count':
      return countFormatter.format(value);
    default:
      return decimalFormatter.format(value);
  }
};

/** Short value-axis tick label ("$1.2K", "1.5K h", "40%") so ticks fit the axis at phone width. */
export const formatChartAxisTick = (value: number, unit: string): string => {
  switch (unit) {
    case 'currency':
      return compactCurrencyFormatter.format(value);
    case 'percent':
      return `${compactFormatter.format(value)}%`;
    case 'hours':
      return `${compactFormatter.format(value)} h`;
    default:
      return compactFormatter.format(value);
  }
};

/** The unit shared by every series in the chart; the server guarantees they agree. */
export const chartUnit = (spec: ChatChartSpecDto): string =>
  spec.series?.[0]?.unit ?? 'number';

const CHART_HEIGHT = 240;
const MIN_ROWS_CHART_HEIGHT = 120;
const MAX_COLUMN_POINTS = 6;
const LONG_LABEL_CHARS = 12;
const ROW_HEIGHT = 28;
const MIN_ROW_HEIGHT = 18;
const ROWS_COMFORT_HEIGHT = 900;
const MAX_LABEL_CHARS = 16;
const MIN_LABEL_CHARS = 6;
const LABEL_WIDTH_SHARE = 0.4;
// Estimates for a 12px axis tick: recharts cannot be measured before render.
const LABEL_CHAR_PX = 8;
export const TICK_FONT_PX = 12;
const CATEGORY_TICK_GAP_PX = 20;
const TILTED_LABEL_EDGE_PX = 8;
// Recharts defaults: the X axis is 30px tall and the Y axis 60px wide.
export const DEFAULT_X_AXIS_HEIGHT = 30;
const DEFAULT_Y_AXIS_WIDTH = 60;
export const LEGEND_HEIGHT = 36;
export const CHART_VERTICAL_MARGIN = 10;
const MAX_TOOLTIP_WIDTH = 220;
export const MIN_TOOLTIP_WIDTH = 120;
export const TILTED_LABEL_ANGLE = -35;

/**
 * How a cartesian chart is laid out. `rows` draws horizontal bars with the
 * categories down the Y axis (Mantine `orientation="vertical"`); `columns`
 * draws categories along the X axis. `tooltipMaxWidth` keeps the tooltip
 * within the plot area, which starts right of the Y axis.
 */
export type ChatChartLayout =
  | {
      kind: 'rows';
      height: number;
      categoryAxisWidth: number;
      labelMaxChars: number;
      integerTicks: boolean;
      tooltipMaxWidth: number;
    }
  | {
      kind: 'columns';
      height: number;
      tilt: {
        axisHeight: number;
        leftMargin: number;
        labelMaxChars: number;
      } | null;
      integerTicks: boolean;
      tooltipMaxWidth: number;
    };

/** True when every drawn value is a whole number, so the value axis never needs fractional ticks. */
const hasOnlyIntegerValues = (spec: ChatChartSpecDto): boolean =>
  (spec.points ?? []).every((point) =>
    (spec.series ?? []).every((_, index) => {
      const value = chartValueAt(point, index);
      return value === null || Number.isInteger(value);
    }),
  );

const labelCharsWithin = (budgetPx: number, pxPerChar: number): number =>
  Math.max(
    MIN_LABEL_CHARS,
    Math.min(MAX_LABEL_CHARS, Math.floor(budgetPx / pxPerChar)),
  );

/**
 * Chooses the layout for a cartesian chart from its data and the width it will be drawn
 * at (0 when not yet measured). Bar charts with more than 6 categories, or any
 * category label longer than 12 characters, switch to horizontal bars so
 * every label can be shown; line and area charts tilt their thinned X labels
 * only when those labels are long.
 */
export const chooseChartLayout = (
  spec: ChatChartSpecDto,
  availableWidth = 0,
): ChatChartLayout => {
  const points = spec.points ?? [];
  const longestLabel = points.reduce(
    (longest, point) => Math.max(longest, (point.x ?? '').length),
    0,
  );
  const integerTicks =
    chartUnit(spec) === 'count' || hasOnlyIntegerValues(spec);
  const legendHeight = (spec.series?.length ?? 0) >= 2 ? LEGEND_HEIGHT : 0;
  const isBar = spec.type === 'bar' || spec.type === 'stacked-bar';
  const measured = availableWidth > 0;
  const labelBudgetPx = measured
    ? availableWidth * LABEL_WIDTH_SHARE
    : Number.POSITIVE_INFINITY;
  const tooltipMaxWidth = (plotLeft: number): number =>
    measured
      ? Math.max(
          MIN_TOOLTIP_WIDTH,
          Math.min(MAX_TOOLTIP_WIDTH, Math.floor(availableWidth - plotLeft)),
        )
      : MAX_TOOLTIP_WIDTH;

  if (
    isBar &&
    (points.length > MAX_COLUMN_POINTS || longestLabel > LONG_LABEL_CHARS)
  ) {
    const rowsHeight = Math.max(
      points.length * MIN_ROW_HEIGHT,
      Math.min(points.length * ROW_HEIGHT, ROWS_COMFORT_HEIGHT),
    );
    const labelMaxChars = labelCharsWithin(
      labelBudgetPx - CATEGORY_TICK_GAP_PX,
      LABEL_CHAR_PX,
    );
    const categoryAxisWidth =
      Math.min(longestLabel, labelMaxChars) * LABEL_CHAR_PX +
      CATEGORY_TICK_GAP_PX;
    return {
      kind: 'rows',
      height: Math.max(
        MIN_ROWS_CHART_HEIGHT,
        rowsHeight +
          DEFAULT_X_AXIS_HEIGHT +
          CHART_VERTICAL_MARGIN +
          legendHeight,
      ),
      categoryAxisWidth,
      labelMaxChars,
      integerTicks,
      tooltipMaxWidth: tooltipMaxWidth(categoryAxisWidth),
    };
  }

  if (isBar || longestLabel <= LONG_LABEL_CHARS) {
    return {
      kind: 'columns',
      height: CHART_HEIGHT,
      tilt: null,
      integerTicks,
      tooltipMaxWidth: tooltipMaxWidth(DEFAULT_Y_AXIS_WIDTH),
    };
  }

  const angle = (Math.abs(TILTED_LABEL_ANGLE) * Math.PI) / 180;
  const labelMaxChars = labelCharsWithin(
    labelBudgetPx,
    LABEL_CHAR_PX * Math.cos(angle),
  );
  const labelPx = Math.min(longestLabel, labelMaxChars) * LABEL_CHAR_PX;
  const axisHeight = Math.ceil(
    labelPx * Math.sin(angle) + TICK_FONT_PX * Math.cos(angle) + TICK_FONT_PX,
  );
  const leftMargin = Math.max(
    0,
    Math.ceil(labelPx * Math.cos(angle)) +
      TILTED_LABEL_EDGE_PX -
      DEFAULT_Y_AXIS_WIDTH,
  );
  return {
    kind: 'columns',
    height: CHART_HEIGHT + axisHeight - DEFAULT_X_AXIS_HEIGHT,
    tilt: { axisHeight, leftMargin, labelMaxChars },
    integerTicks,
    tooltipMaxWidth: tooltipMaxWidth(DEFAULT_Y_AXIS_WIDTH + leftMargin),
  };
};

/** Cuts a category label to `maxChars` characters, ending it with an ellipsis when shortened. */
export const truncateChartLabel = (label: string, maxChars: number): string =>
  label.length > maxChars
    ? `${label.slice(0, maxChars - 1).trimEnd()}…`
    : label;
